// Ranking: deterministic, explainable, diet-filtered BEFORE scoring.
// All matching is by canonical id membership (with parent/child lineage), never substrings.

import { judgeIngredients, type Caution, type DietRuleId } from './diet';
import { createStock, type StockItem } from './inventory';
import type { Recipe, RecipeIngredient } from './recipes';
import { substitutesFor, type Substitution } from './substitutions';
import { displayName, INGREDIENTS, lineage, type IngredientId } from './taxonomy';

export type CookingStyle = 'quick' | 'easy_only' | 'one_pot' | 'budget' | 'high_protein';

export interface RankContext {
  /** Confirmed kitchen items (never unreviewed detections). */
  kitchen: IngredientId[];
  /** Staples the user confirmed they keep. */
  staples: IngredientId[];
  /** Kitchen items the user flagged "use soon". */
  useSoon: IngredientId[];
  rules: DietRuleId[];
  dislikes: IngredientId[];
  styles?: CookingStyle[];
  /** Recipe ids cooked recently (most recent first), for variety. */
  recentlyCooked?: string[];
  /** Amounts the user entered for kitchen items; items without one count as enough. */
  stock?: StockItem[];
  /** Servings to check amounts against; defaults to each recipe's own. */
  servings?: number;
}

export interface RankedRecipe {
  recipe: Recipe;
  makeable: boolean;
  /** Required ingredients not owned and with no safe substitute, or owned but not enough. */
  missing: IngredientId[];
  /** The subset of `missing` the user has some of, just not enough. */
  short: IngredientId[];
  substitutions: Substitution[];
  /** Optional ingredients left out because they break a rule or are disliked. */
  omitted: IngredientId[];
  /** Kitchen items (not staples) this recipe uses. */
  usesFromKitchen: IngredientId[];
  usesSoon: IngredientId[];
  cautions: Caution[];
  score: number;
  reason: string;
}

/** Expand owned ids so that owning a child satisfies its ancestors (cherry tomatoes → tomatoes). */
export function ownedSet(ids: Iterable<IngredientId>): Set<IngredientId> {
  const out = new Set<IngredientId>();
  for (const id of ids) for (const x of lineage(id)) out.add(x);
  return out;
}

function dislikedBy(id: IngredientId, dislikes: Set<IngredientId>): boolean {
  return lineage(id).some((x) => dislikes.has(x));
}

export interface Adapted {
  ingredients: RecipeIngredient[];
  omitted: IngredientId[];
}

/**
 * Apply the user's hard rules to a recipe. Returns null when a required ingredient breaks a rule
 * or is disliked (recipe hidden). Optional ingredients that break a rule are dropped instead.
 */
export function adaptForRules(recipe: Recipe, rules: DietRuleId[], dislikes: IngredientId[]): (Adapted & { cautions: Caution[] }) | null {
  const dis = new Set(dislikes);
  const kept: RecipeIngredient[] = [];
  const omitted: IngredientId[] = [];
  for (const ing of recipe.ingredients) {
    const bad = judgeIngredients([ing.id], rules).blocked || dislikedBy(ing.id, dis);
    if (!bad) kept.push(ing);
    else if (ing.role === 'optional') omitted.push(ing.id);
    else return null;
  }
  const { cautions } = judgeIngredients(kept.map((i) => i.id), rules);
  return { ingredients: kept, omitted, cautions };
}

export function rankRecipes(recipes: Recipe[], ctx: RankContext): RankedRecipe[] {
  const kitchen = ownedSet(ctx.kitchen);
  const owned = ownedSet([...ctx.kitchen, ...ctx.staples]);
  const useSoon = new Set(ctx.useSoon);
  const dis = new Set(ctx.dislikes);
  const styles = new Set(ctx.styles ?? []);
  const recent = ctx.recentlyCooked ?? [];

  const out: RankedRecipe[] = [];
  for (const recipe of recipes) {
    const adapted = adaptForRules(recipe, ctx.rules, ctx.dislikes);
    if (!adapted) continue;

    const missing: IngredientId[] = [];
    const short: IngredientId[] = [];
    const substitutions: Substitution[] = [];
    const usesFromKitchen: IngredientId[] = [];
    const stock = createStock(ctx.stock ?? []);
    const scale = (ctx.servings ?? recipe.servings) / recipe.servings;

    for (const ing of adapted.ingredients) {
      if (kitchen.has(ing.id)) usesFromKitchen.push(ing.id);
      if (ing.role === 'optional') continue;
      if (owned.has(ing.id)) {
        if (stock.draw(ing, scale) > 0) { missing.push(ing.id); short.push(ing.id); }
        continue;
      }
      const swap = substitutesFor(ing.id).find(
        (s) => owned.has(s.to) && !judgeIngredients([s.to], ctx.rules).blocked && !dislikedBy(s.to, dis),
      );
      if (swap) {
        substitutions.push(swap);
        if (kitchen.has(swap.to)) usesFromKitchen.push(swap.to);
      } else {
        missing.push(ing.id);
      }
    }

    const makeable = missing.length === 0;
    // The user's own "use soon" items that satisfy one of this recipe's ingredients.
    const needed = new Set([...adapted.ingredients.map((i) => i.id), ...substitutions.map((s) => s.to)]);
    const usesSoon = [...useSoon].filter((u) => lineage(u).some((x) => needed.has(x)));
    const coreCount = adapted.ingredients.filter((i) => i.role === 'core').length || 1;
    const coreFromKitchen = adapted.ingredients.filter((i) => i.role === 'core' && kitchen.has(i.id)).length;

    let score = 0;
    if (makeable) score += 100;
    score -= missing.length * 15;
    score += (coreFromKitchen / coreCount) * 40;
    score += usesSoon.length * 30;
    score -= substitutions.length * 4;
    score += Math.max(0, 30 - recipe.minutes) * 0.4;
    const recentIdx = recent.indexOf(recipe.id);
    if (recentIdx >= 0) score -= Math.max(10, 40 - recentIdx * 5);
    if (styles.has('quick') && recipe.minutes <= 20) score += 12;
    if (styles.has('easy_only') && recipe.difficulty === 'easy') score += 8;
    if (styles.has('one_pot') && (recipe.tags.includes('one_pot') || recipe.tags.includes('sheet_pan'))) score += 8;
    if (styles.has('budget') && recipe.tags.includes('budget')) score += 8;
    if (styles.has('high_protein') && recipe.tags.includes('high_protein')) score += 8;

    out.push({
      recipe,
      makeable,
      missing,
      short,
      substitutions,
      omitted: adapted.omitted,
      usesFromKitchen: [...new Set(usesFromKitchen)],
      usesSoon,
      cautions: adapted.cautions,
      score,
      reason: buildReason(makeable, missing, short, usesSoon, coreFromKitchen),
    });
  }

  return out.sort((a, b) => (a.makeable === b.makeable ? b.score - a.score : a.makeable ? -1 : 1));
}

function buildReason(makeable: boolean, missing: IngredientId[], short: IngredientId[], usesSoon: IngredientId[], fromKitchen: number): string {
  const parts: string[] = [];
  if (usesSoon.length) parts.push(`Uses your ${displayName(usesSoon[0])}${usesSoon.length > 1 ? ` + ${usesSoon.length - 1} more` : ''} (marked use soon)`);
  if (makeable) parts.push(fromKitchen > 0 ? 'You have everything' : 'Pantry staples only');
  else {
    const name = (id: IngredientId) => (short.includes(id) ? `more ${displayName(id)}` : displayName(id));
    parts.push(`Missing ${missing.length}: ${missing.slice(0, 2).map(name).join(', ')}${missing.length > 2 ? '…' : ''}`);
  }
  return parts.join(' · ');
}

/** The first protein-category ingredient, used by the planner for variety. */
export function mainProtein(recipe: Recipe): IngredientId | null {
  const hit = recipe.ingredients.find((i) => i.role === 'core' && INGREDIENTS[i.id].category === 'protein');
  return hit ? lineage(hit.id).at(-1) ?? hit.id : null;
}

/** One confident pick plus up to two different swaps. Mains only. */
export function tonightPicks(ranked: RankedRecipe[]): { pick: RankedRecipe | null; swaps: RankedRecipe[] } {
  const mains = ranked.filter((r) => r.recipe.meal === 'main');
  const pick = mains[0] ?? null;
  const swaps = mains
    .slice(1)
    .filter((r) => !pick || mainProtein(r.recipe) !== mainProtein(pick.recipe) || r.makeable)
    .slice(0, 2);
  return { pick, swaps };
}
